import {
    Caption1,
    Card,
    CardHeader,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    makeStyles,
    OverlayDrawer,
    Spinner,
    useRestoreFocusSource,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useSelector } from '../../../../../store/hooks';
import React, { useEffect } from 'react';
import { loadBroadcastMessageVersions } from '../../../../../store/features/broadcastMessageReport/broadcastMessageReportSlice.thunks';
import { BroadcastMessageVersion } from '../../../../../model';
import { formatDateUtc } from '../../../../../i18n';
import { CloseButton } from '../../../../../controls/Buttons/CloseButton';
import BroadcastMessageStats from '../BroadcastMessageStats/BroadcastMessageStats';

const useListStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
});

export interface BroadcastMessageVersionsDrawerProps {
    open: boolean;
    messageId: string | null;
    onOpenChange?: (open: boolean) => void;
}

const VersionList: React.FC<{ items: BroadcastMessageVersion[] }> = ({
    items,
}) => {
    const styles = useListStyles();

    return (
        <div className={styles.root}>
            {items.map((x) => {
                return (
                    <Card key={x.id}>
                        <CardHeader
                            header={<b>{x.title}</b>}
                            description={
                                <Caption1>
                                    {formatDateUtc(
                                        x.createdOnUtc,
                                        'DD/MM/YY HH:mm'
                                    ) + ' (UTC)'}
                                </Caption1>
                            }
                        />
                        {x.content}
                        <BroadcastMessageStats data={x.stats} />
                    </Card>
                );
            })}
        </div>
    );
};

const BroadcastMessageVersionsDrawer: React.FC<
    BroadcastMessageVersionsDrawerProps
> = ({ open, messageId, onOpenChange }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const { versions } = useSelector((x) => x.broadcastMessageReport);

    const restoreFocusSourceAttributes = useRestoreFocusSource();

    useEffect(() => {
        if (!messageId) {
            return;
        }

        dispatch(loadBroadcastMessageVersions(messageId));
    }, [messageId, dispatch]);

    return (
        <OverlayDrawer
            {...restoreFocusSourceAttributes}
            open={open}
            position="end"
            onOpenChange={(_, data) => onOpenChange?.(data.open)}
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <CloseButton onClick={() => onOpenChange?.(false)} />
                    }
                >
                    {t('Broadcast_MessageVersions')}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                {versions.loadStatus === 'loading' ? (
                    <Spinner />
                ) : (
                    <VersionList items={versions.items} />
                )}
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default BroadcastMessageVersionsDrawer;
